import { api } from "@/lib/api";

export type PartnerRequestStatus =
  | "PENDING"
  | "UNDER_REVIEW"
  | "NEEDS_REVISION"
  | "ACCEPTED"
  | "REJECTED";

export interface PartnerRequest {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  website: string | null;
  status: PartnerRequestStatus;
  rejectionReason?: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    name: string | null;
    email: string;
  };
}

export interface GetPartnerRequestsDto {
  page?: number;
  limit?: number;
  status?: PartnerRequestStatus;
  search?: string;
}

export interface PartnerRequestsPagination {
  items: PartnerRequest[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  statusCounts: Record<string, number>;
}

export class PartnerProgramService {
  private static readonly BASE_URL = "/partner-program";

  static async getRequests(
    params: GetPartnerRequestsDto = {},
  ): Promise<PartnerRequestsPagination> {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.append("page", params.page.toString());
    if (params.limit !== undefined)
      query.append("limit", params.limit.toString());
    if (params.status) query.append("status", params.status);
    if (params.search) query.append("search", params.search);

    const queryString = query.toString();
    const url = `${this.BASE_URL}/requests${queryString ? `?${queryString}` : ""}`;
    return api.get<PartnerRequestsPagination>(url);
  }

  static async getStats(): Promise<Record<string, number>> {
    return api.get<Record<string, number>>(`${this.BASE_URL}/requests/stats`);
  }

  static async getRequest(id: string): Promise<PartnerRequest> {
    return api.get<PartnerRequest>(`${this.BASE_URL}/requests/${id}`);
  }

  static async approve(id: string): Promise<PartnerRequest> {
    return api.patch<PartnerRequest>(`${this.BASE_URL}/approve/${id}`, {});
  }

  static async reject(id: string, reason: string): Promise<PartnerRequest> {
    return api.patch<PartnerRequest>(`${this.BASE_URL}/reject/${id}`, {
      reason,
    });
  }

  static async requestRevision(
    id: string,
    reason: string,
  ): Promise<PartnerRequest> {
    return api.patch<PartnerRequest>(`${this.BASE_URL}/revision/${id}`, {
      reason,
    });
  }

  static async updateStatus(
    id: string,
    status: PartnerRequestStatus,
  ): Promise<PartnerRequest> {
    return api.patch<PartnerRequest>(`${this.BASE_URL}/status/${id}`, {
      status,
    });
  }
}
